<script context="module" lang="ts">
  import { getApiPaths } from '$lib/stores/getUserStore';
  import type { Load } from '@sveltejs/kit';

  import { API } from 'shared/src/utils/api/api';

  interface TestInfo {
    testName: string;
    message: string;
  }

  export const load: Load = async ({ params, fetch, url }) => {
    const api = new API(getApiPaths(), fetch);

    const testInviteLink = params.testInviteLink;
    if (!testInviteLink) {
      return {
        status: 400,
        error: 'An invite link must be provided to join a section',
      };
    }

    const externalUniqueId = url.searchParams.get('externalUniqueId');

    try {
      const testInfo = await api.$get<TestInfo>(
        `/testing/test-info/${testInviteLink}/${externalUniqueId || ''}`
      );
      return {
        props: {
          testInfo,
          isInvalid: false,
          testInviteLink,
          externalUniqueId,
        },
      };
    } catch (err: any) {
      console.error(err);
      if (err?.status === 404 || err?.status === 400) {
        return {
          props: {
            isInvalid: true,
            testInviteLink,
            errorMessage: err?.message,
          },
        };
      }
      return {
        status: err?.status || 500,
        error: err?.message || 'Unable to join that section.',
      };
    }
  };
</script>

<script lang="ts">
  import H from '$lib/components/H.svelte';
  import PageTitle from '$lib/teach/resources/PageTitle.svelte';
  import LoginRegisterTabs from '$lib/app/authentication/LoginRegisterTabs.svelte';
  import { goto } from '$app/navigation';
    import { openModal } from '$lib/stores/modals.store';
  export let testInfo: TestInfo | undefined = undefined;
  export let testInviteLink: string;
  export let externalUniqueId: string | null;
  export let isInvalid: boolean;
  export let errorMessage: string | undefined;
</script>

<div class="article">
  <PageTitle title="Add a uTheory Test" doNotAutoCapitalize description="Add a uTheory Test" />
  {#if testInfo && !isInvalid}
    <H level={2}>You've been invited to take the {testInfo.testName}</H>
    <p>
      {testInfo.message}
    </p>
  {:else}
    <H level={2} class="text-red">That link is not valid</H>
    <div class="bg-red-50 p-2 mb-2 text-red-900">
      {#if errorMessage}
        <p>{errorMessage}</p>
        <p>
          You may still continue to register or sign in to uTheory, but you will not have access to
          that test until you get a working link from your teacher.
        </p>
      {:else}
        <p class="text-red-900">
          Sorry, that test invitation link is not valid. Please ask your teacher for a new link.
        </p>
        <p>
          You can continue to register as an independent user, and join your teacher's test later
          using a new test link.
        </p>
      {/if}
    </div>
  {/if}
</div>

<LoginRegisterTabs
  parameters={{
    testInviteLink: isInvalid ? undefined : testInviteLink,
    externalUniqueId: isInvalid || !externalUniqueId ? undefined : externalUniqueId,
  }}
  on:loginComplete={() => {
    goto('/app');
  }}
  on:registrationComplete={() => {
    goto('/app');
  }}
  on:forgottenPassword={() => {
    openModal('passwordReset');
  }}
/>
